import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Wrench, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  dismissMaintenanceNotice,
  filterVisibleMaintenanceBanners,
  readDismissedMaintenanceIds,
} from "@/lib/maintenance-notices";

/**
 * Platform maintenance strip — shown on marketing home, admin, and login only.
 * Do not mount on event landing or registration pages.
 */
export default function MaintenanceNoticeBanner({ banners, className = "" }) {
  const [dismissedIds, setDismissedIds] = useState(() => readDismissedMaintenanceIds());

  const visibleBanners = useMemo(
    () => filterVisibleMaintenanceBanners(banners, dismissedIds),
    [banners, dismissedIds],
  );

  const dismiss = useCallback((noticeId) => {
    dismissMaintenanceNotice(noticeId);
    setDismissedIds(readDismissedMaintenanceIds());
  }, []);

  if (!visibleBanners.length) return null;

  return (
    <div className={`space-y-0 shrink-0 ${className}`} data-testid="maintenance-notice-banner">
      {visibleBanners.map((notice) => {
        const inProgress = notice.severity === "in_progress";
        const Icon = inProgress ? Wrench : AlertTriangle;
        return (
          <div
            key={notice.id}
            className="flex flex-wrap items-start gap-3 px-4 py-3 text-sm border-b"
            style={{
              background: inProgress
                ? "color-mix(in srgb, var(--primary) 12%, transparent)"
                : "rgba(234, 179, 8, 0.1)",
              borderColor: "var(--border-default)",
              color: "var(--text-primary)",
            }}
            role="status"
          >
            <Icon size={16} className="shrink-0 mt-0.5" style={{ color: inProgress ? "var(--primary)" : "#eab308" }} />
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium">{notice.title}</div>
              {notice.body ? (
                <p className="mt-1 text-[var(--text-secondary)] leading-relaxed">{notice.body}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Link to="/status" className="text-xs font-mono uppercase tracking-wider text-[var(--primary)] hover:underline">
                View status
              </Link>
              <button
                type="button"
                onClick={() => dismiss(notice.id)}
                className="inline-flex items-center justify-center w-7 h-7 border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-hover)] transition-colors"
                aria-label={`Dismiss maintenance notice: ${notice.title}`}
                data-testid={`dismiss-maintenance-${notice.id}`}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
